import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Ticket, TrendingUp, Users, Car, RefreshCw, Clock, ChevronLeft, ChevronRight, Calendar, BarChart3, Sun, Cloud, CloudRain, CloudSnow, CloudLightning, CloudDrizzle, Wind } from 'lucide-react';
import { mijneTicketsService, peopleCountingService, statsService } from '../lib/supabaseClient';
import { useTheme } from '../App';

const OverzichtDashboard = ({ showNotification, isActive = true }) => {
  const { t } = useTheme();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const datePickerRef = useRef(null);

  const [currentTime, setCurrentTime] = useState(new Date());

  // Data states
  const [ticketScanStats, setTicketScanStats] = useState([]);
  const [ticketSalesStats, setTicketSalesStats] = useState([]);
  const [visitorStats, setVisitorStats] = useState(null);
  const [visitorHourly, setVisitorHourly] = useState([]);
  const [kentekenStats, setKentekenStats] = useState(null);
  const [weather, setWeather] = useState(null);

  // Check if viewing a past day
  const isYesterday = useCallback(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selected = new Date(selectedDate);
    selected.setHours(0, 0, 0, 0);
    return selected < today;
  }, [selectedDate]);

  // Format date for display
  const formatDateDisplay = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selected = new Date(date);
    selected.setHours(0, 0, 0, 0);

    if (selected.getTime() === today.getTime()) return 'Vandaag';

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (selected.getTime() === yesterday.getTime()) return 'Gisteren';

    return selected.toLocaleDateString('nl-NL', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
  };

  const goToPreviousDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    setSelectedDate(newDate);
  };

  const goToToday = () => {
    setSelectedDate(new Date());
    setShowDatePicker(false);
  };

  const canGoNext = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selected = new Date(selectedDate);
    selected.setHours(0, 0, 0, 0);
    return selected < today;
  };

  const goToNextDay = () => {
    if (canGoNext()) {
      const newDate = new Date(selectedDate);
      newDate.setDate(newDate.getDate() + 1);
      setSelectedDate(newDate);
    }
  };

  const handleDateSelect = (dateString) => {
    setSelectedDate(new Date(dateString));
    setShowDatePicker(false);
  };

  // Format date for API (YYYY-MM-DD HH:MM:SS)
  const formatDateForApi = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  const formatDateForInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getTodayString = () => formatDateForInput(new Date());

  // Live clock
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Weersverwachting via Open-Meteo (gratis, geen API key)
  const loadWeather = useCallback(async () => {
    try {
      const res = await fetch(
        'https://api.open-meteo.com/v1/forecast?latitude=52.1803&longitude=6.6969' +
        '&hourly=temperature_2m,precipitation_probability,weathercode' +
        '&timezone=Europe%2FAmsterdam&forecast_days=2'
      );
      const json = await res.json();
      const now = new Date();
      const currentHour = now.getHours();
      const todayStr = now.toISOString().slice(0, 10);

      // Zoek de index van het huidige uur in de tijdreeks
      const startIdx = json.hourly.time.findIndex(t => t === `${todayStr}T${String(currentHour).padStart(2, '0')}:00`);
      if (startIdx === -1) return;

      const hours = [];
      for (let i = 0; i < 3; i++) {
        const idx = startIdx + i;
        hours.push({
          time: json.hourly.time[idx],
          temp: Math.round(json.hourly.temperature_2m[idx]),
          rain: json.hourly.precipitation_probability[idx],
          code: json.hourly.weathercode[idx],
        });
      }
      setWeather(hours);
    } catch (err) {
      console.error('Weer laden mislukt:', err);
    }
  }, []);

  useEffect(() => {
    loadWeather();
    const timer = setInterval(loadWeather, 30 * 60 * 1000);
    return () => clearInterval(timer);
  }, [loadWeather]);

  // Close date picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setShowDatePicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadData = useCallback(async (isAutoRefresh = false) => {
    try {
      if (!isAutoRefresh) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      const dayStart = new Date(selectedDate);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(selectedDate);
      dayEnd.setHours(23, 59, 59, 999);

      // Fetch all data sources in parallel
      const promises = [];

      // 1. Ticket scan stats (MijnETickets)
      if (mijneTicketsService.isConfigured()) {
        promises.push(
          mijneTicketsService.getScanStats(
            formatDateForApi(dayStart),
            formatDateForApi(dayEnd),
            '10'
          ).catch(err => {
            console.error('Error loading ticket scan stats:', err);
            return null;
          })
        );
        // 2. Ticket sales stats
        promises.push(
          mijneTicketsService.getSalesStats(
            formatDateForApi(dayStart),
            formatDateForApi(dayEnd),
            'hour'
          ).catch(err => {
            console.error('Error loading ticket sales stats:', err);
            return null;
          })
        );
      } else {
        promises.push(Promise.resolve(null));
        promises.push(Promise.resolve(null));
      }

      // 3. Visitor stats (People Counting)
      const isToday = !isYesterday();
      if (isToday) {
        promises.push(
          peopleCountingService.getDashboardStats().catch(err => {
            console.error('Error loading visitor stats:', err);
            return null;
          })
        );
        promises.push(
          peopleCountingService.getTodayHourly().catch(err => {
            console.error('Error loading visitor hourly:', err);
            return [];
          })
        );
      } else {
        const eventDayStart = new Date(selectedDate);
        eventDayStart.setHours(5, 0, 0, 0);
        const eventDayEnd = new Date(selectedDate);
        eventDayEnd.setDate(eventDayEnd.getDate() + 1);
        eventDayEnd.setHours(4, 59, 59, 999);

        promises.push(
          peopleCountingService.getHistorical(
            eventDayStart.toISOString(),
            eventDayEnd.toISOString(),
            'Hour'
          ).then(historicalData => {
            const totalEntered = historicalData.reduce((sum, h) => sum + (h.entered_total || 0), 0);
            const totalExited = historicalData.reduce((sum, h) => sum + (h.exited_total || 0), 0);
            const peakInside = Math.max(...historicalData.map(h => h.inside_total || 0), 0);
            return {
              current_hour: null,
              today: { entered: totalEntered, exited: totalExited, peak_inside: peakInside }
            };
          }).catch(err => {
            console.error('Error loading historical visitor stats:', err);
            return null;
          })
        );
        promises.push(
          peopleCountingService.getHistorical(
            eventDayStart.toISOString(),
            eventDayEnd.toISOString(),
            'Hour'
          ).catch(err => {
            console.error('Error loading historical visitor hourly:', err);
            return [];
          })
        );
      }

      // 4. Kenteken stats
      promises.push(
        statsService.getDashboardStats().catch(err => {
          console.error('Error loading kenteken stats:', err);
          return null;
        })
      );

      const [scansData, salesData, visitorData, visitorHourlyData, kentekenData] = await Promise.all(promises);

      // Process ticket scan stats
      if (scansData) {
        const scansPerInterval = scansData.scans_per_interval || {};
        const scansArray = Object.entries(scansPerInterval).map(([period, scans]) => ({
          period,
          scans
        }));
        setTicketScanStats(scansArray);
      } else {
        setTicketScanStats([]);
      }

      // Process ticket sales stats
      if (salesData) {
        const salesTickets = salesData.tickets || [];
        const salesAggregated = {};
        salesTickets.forEach(ticketType => {
          if (ticketType.values && typeof ticketType.values === 'object' && !Array.isArray(ticketType.values)) {
            Object.entries(ticketType.values).forEach(([datetime, count]) => {
              if (!salesAggregated[datetime]) salesAggregated[datetime] = 0;
              salesAggregated[datetime] += count;
            });
          }
        });
        const salesArray = Object.entries(salesAggregated).map(([period, tickets_sold]) => ({
          period,
          tickets_sold
        }));
        setTicketSalesStats(salesArray);
      } else {
        setTicketSalesStats([]);
      }

      // Set visitor data
      setVisitorStats(visitorData);
      setVisitorHourly(visitorHourlyData || []);

      // Set kenteken data
      setKentekenStats(kentekenData);

    } catch (err) {
      console.error('Error loading overview data:', err);
      showNotification('Fout bij laden overzicht: ' + err.message, 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedDate, isYesterday, showNotification]);

  // Load data on mount and when date changes
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-refresh every 60 seconds (only for today)
  useEffect(() => {
    if (autoRefresh && isActive && !isYesterday()) {
      const interval = setInterval(() => {
        loadData(true);
      }, 60000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, isActive, isYesterday, loadData]);

  // Helper to safely parse datetime string
  const parseDateTime = (dateStr) => new Date(dateStr.replace(' ', 'T'));

  // Calculate totals
  const totals = {
    ticketsScanned: ticketScanStats.reduce((sum, s) => sum + (s.scans || 0), 0),
    ticketsSold: ticketSalesStats.reduce((sum, s) => sum + (s.tickets_sold || 0), 0),
    visitorsEntered: visitorStats?.today?.entered || 0,
    visitorsExited: visitorStats?.today?.exited || 0,
    visitorsPeak: visitorStats?.today?.peak_inside || 0,
    visitorsInside: visitorStats?.current_hour?.inside || 0,
    kentekenScans: kentekenStats?.scans_today || 0,
    totalKentekens: kentekenStats?.total_vehicles || 0,
  };

  // Build combined hourly data for chart
  const getCombinedHourlyData = () => {
    const hourlyMap = {};

    // Aggregate ticket scans per hour
    ticketScanStats.forEach(stat => {
      const time = parseDateTime(stat.period);
      const hour = time.getHours();
      if (!hourlyMap[hour]) hourlyMap[hour] = { hour, ticketScans: 0, visitorIn: 0 };
      hourlyMap[hour].ticketScans += stat.scans || 0;
    });

    // Aggregate visitor data per hour
    visitorHourly.forEach(h => {
      const time = new Date(h.start_time);
      const hour = time.getHours();
      if (!hourlyMap[hour]) hourlyMap[hour] = { hour, ticketScans: 0, visitorIn: 0 };
      hourlyMap[hour].visitorIn += h.entered_total || 0;
    });

    // Convert to sorted array
    return Object.values(hourlyMap).sort((a, b) => a.hour - b.hour);
  };

  const combinedHourly = getCombinedHourlyData();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-zf-accent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="grid grid-cols-3 items-center">
        <div>
          <h1 className={`text-3xl font-bold ${t.text}`}>Overzicht</h1>
          <p className={`${t.textSecondary} mt-1`}>Gecombineerd overzicht van alle bronnen</p>
        </div>
        <div className="text-center">
          <p className={`text-4xl font-bold tabular-nums ${t.text}`}>
            {currentTime.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </p>
          <p className={`text-sm ${t.textSecondary} mt-1`}>
            {currentTime.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
        </div>
        <div className="flex gap-2 justify-end">
          <button
            onClick={() => loadData()}
            disabled={refreshing}
            className="bg-zf-accent text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-zf-accent/80 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Ververs
          </button>
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 border ${t.border} ${autoRefresh ? 'bg-zf-accent text-white' : `${t.bgSecondary} ${t.text}`} transition-colors`}
          >
            <Clock className="w-4 h-4" />
            Auto ({autoRefresh ? 'aan' : 'uit'})
          </button>
        </div>
      </div>

      {/* Weersvoorspelling komende 3 uur */}
      {weather && (() => {
        const getWeatherInfo = (code) => {
          if (code === 0)               return { Icon: Sun,           label: 'Helder' };
          if (code <= 2)                return { Icon: Cloud,          label: 'Licht bewolkt' };
          if (code === 3)               return { Icon: Cloud,          label: 'Bewolkt' };
          if (code <= 48)               return { Icon: Wind,           label: 'Mist' };
          if (code <= 55)               return { Icon: CloudDrizzle,   label: 'Motregen' };
          if (code <= 65)               return { Icon: CloudRain,      label: 'Regen' };
          if (code <= 77)               return { Icon: CloudSnow,      label: 'Sneeuw' };
          if (code <= 82)               return { Icon: CloudRain,      label: 'Buien' };
          return                               { Icon: CloudLightning,  label: 'Onweer' };
        };

        return (
          <div className={`${t.card} rounded-xl shadow-lg border`}>
            <div className="flex">
              {weather.map((h, i) => {
                const { Icon, label } = getWeatherInfo(h.code);
                const hour = new Date(h.time).getHours();
                const timeLabel = i === 0 ? 'Nu' : `${String(hour).padStart(2, '0')}:00`;
                return (
                  <div key={i} className={`flex-1 p-4 text-center ${i > 0 ? `border-l ${t.border}` : ''}`}>
                    <p className={`text-sm ${t.textSecondary} mb-2`}>{timeLabel}</p>
                    <Icon className={`w-8 h-8 mx-auto mb-2 ${i === 0 ? 'text-zf-accent' : t.text}`} />
                    <p className={`text-2xl font-bold ${t.text}`}>{h.temp}°C</p>
                    <p className={`text-xs ${t.textSecondary} mt-1`}>{label}</p>
                    <p className={`text-xs mt-1 ${h.rain >= 50 ? 'text-blue-400' : t.textMuted}`}>
                      {h.rain}% neerslag
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Date Navigation */}
      <div className={`${t.card} rounded-xl shadow-lg p-4 border`}>
        <div className="flex items-center justify-between">
          <button
            onClick={goToPreviousDay}
            className={`p-2 rounded-lg ${t.hover} ${t.text}`}
            title="Vorige dag"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          <div className="flex items-center gap-4 relative" ref={datePickerRef}>
            <button
              onClick={() => setShowDatePicker(!showDatePicker)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg ${t.hover} transition-colors`}
            >
              <Calendar className={`w-5 h-5 ${t.text}`} />
              <span className={`text-xl font-semibold ${t.text}`}>
                {formatDateDisplay(selectedDate)}
              </span>
            </button>

            {showDatePicker && (
              <div
                className={`absolute top-full mt-2 left-1/2 transform -translate-x-1/2 z-50 ${t.card} rounded-xl shadow-xl border p-4`}
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="date"
                  value={formatDateForInput(selectedDate)}
                  max={getTodayString()}
                  onChange={(e) => handleDateSelect(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  className={`${t.input} border rounded-lg px-4 py-2 focus:ring-2 focus:ring-zf-accent focus:border-transparent outline-none`}
                />
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={(e) => { e.stopPropagation(); goToToday(); }}
                    className="flex-1 bg-zf-accent text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-zf-accent/80 transition-colors"
                  >
                    Vandaag
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const yesterday = new Date();
                      yesterday.setDate(yesterday.getDate() - 1);
                      setSelectedDate(yesterday);
                      setShowDatePicker(false);
                    }}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border ${t.border} ${t.text} ${t.hover} transition-colors`}
                  >
                    Gisteren
                  </button>
                </div>
              </div>
            )}

            {isYesterday() && (
              <button
                onClick={goToToday}
                className="text-zf-accent hover:text-zf-accent/80 text-sm font-medium"
              >
                Naar vandaag
              </button>
            )}
          </div>

          <button
            onClick={goToNextDay}
            disabled={!canGoNext()}
            className={`p-2 rounded-lg ${canGoNext() ? `${t.hover} ${t.text}` : `${t.textMuted} cursor-not-allowed`}`}
            title="Volgende dag"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Live stats (only for today) */}
      {!isYesterday() && (
        <div>
          <h2 className={`text-xl font-semibold ${t.text} mb-4`}>Live Status</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <StatCard
              title="Momenteel Binnen (camera)"
              value={totals.visitorsInside}
              icon={<Users className="w-6 h-6" />}
              color="accent2"
            />
            <StatCard
              title="Momenteel Binnen (tickets)"
              value={Math.max(totals.ticketsScanned - totals.visitorsExited, 0)}
              icon={<Ticket className="w-6 h-6" />}
              color="accent"
            />
            <StatCard
              title="Peak Bezoekers"
              value={totals.visitorsPeak}
              icon={<TrendingUp className="w-6 h-6" />}
              color="accent"
            />
            <StatCard
              title="Kenteken Scans Vandaag"
              value={totals.kentekenScans}
              icon={<Car className="w-6 h-6" />}
              color="success"
            />
          </div>
        </div>
      )}

      {/* Day Totals */}
      <div>
        <h2 className={`text-xl font-semibold ${t.text} mb-4`}>
          {isYesterday() ? 'Dagstatistieken' : 'Vandaag Totaal'}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Tickets Gescand"
            value={totals.ticketsScanned}
            icon={<Ticket className="w-6 h-6" />}
            color="accent"
          />
          <StatCard
            title="Tickets Verkocht"
            value={totals.ticketsSold}
            icon={<TrendingUp className="w-6 h-6" />}
            color="success"
          />
          <StatCard
            title="Bezoekers In (camera)"
            value={totals.visitorsEntered}
            icon={<Users className="w-6 h-6" />}
            color="accent2"
          />
          <StatCard
            title="Bezoekers Uit (camera)"
            value={totals.visitorsExited}
            icon={<Users className="w-6 h-6" />}
            color="error"
          />
        </div>
      </div>

      {/* Combined Hourly Chart */}
      <div>
        <h2 className={`text-xl font-semibold ${t.text} mb-4`}>
          Uuroverzicht {formatDateDisplay(selectedDate)}
        </h2>
        <div className={`${t.card} rounded-xl shadow-lg p-6 border`}>
          {combinedHourly.length > 0 ? (
            <div className="relative">
              {/* Legend */}
              <div className="flex gap-6 mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-zf-accent rounded-full"></div>
                  <span className={`text-sm ${t.textSecondary}`}>Tickets gescand (cumulatief)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
                  <span className={`text-sm ${t.textSecondary}`}>Bezoekers binnen (cumulatief)</span>
                </div>
              </div>

              {/* SVG Dual Line Chart */}
              {(() => {
                const data = combinedHourly;

                // Cumulatieve sommen per uur
                let cumTickets = 0, cumVisitors = 0;
                const cumulativeData = data.map(d => {
                  cumTickets += d.ticketScans;
                  cumVisitors += d.visitorIn;
                  return { hour: d.hour, ticketScans: cumTickets, visitorIn: cumVisitors };
                });

                const maxVal = Math.max(
                  ...cumulativeData.map(d => Math.max(d.ticketScans, d.visitorIn)),
                  1
                );

                // Chart dimensions
                const chartHeight = 200;
                const chartWidth = 930;
                const offsetX = 50;
                const offsetY = 60;

                // Find time range (uren ongewijzigd)
                const hours = data.map(d => d.hour);
                const minHour = Math.min(...hours);
                const maxHour = Math.max(...hours);
                const timeRange = Math.max(maxHour - minHour, 1);

                // Generate points for ticket scans (cumulatief)
                const ticketPoints = cumulativeData.map(d => {
                  const x = offsetX + ((d.hour - minHour) / timeRange) * chartWidth;
                  const y = offsetY + chartHeight - (d.ticketScans / maxVal) * chartHeight;
                  return { x, y, value: d.ticketScans, hour: d.hour };
                });

                // Generate points for visitor counts (cumulatief)
                const visitorPoints = cumulativeData.map(d => {
                  const x = offsetX + ((d.hour - minHour) / timeRange) * chartWidth;
                  const y = offsetY + chartHeight - (d.visitorIn / maxVal) * chartHeight;
                  return { x, y, value: d.visitorIn, hour: d.hour };
                });

                const ticketPath = ticketPoints.map((p, i) =>
                  `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`
                ).join(' ');

                const visitorPath = visitorPoints.map((p, i) =>
                  `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`
                ).join(' ');

                // Area fill for tickets
                const ticketArea = ticketPoints.length > 0
                  ? ticketPath + ` L ${ticketPoints[ticketPoints.length - 1].x} ${offsetY + chartHeight} L ${ticketPoints[0].x} ${offsetY + chartHeight} Z`
                  : '';

                // Area fill for visitors
                const visitorArea = visitorPoints.length > 0
                  ? visitorPath + ` L ${visitorPoints[visitorPoints.length - 1].x} ${offsetY + chartHeight} L ${visitorPoints[0].x} ${offsetY + chartHeight} Z`
                  : '';

                // X-axis labels
                const xLabels = [];
                for (let h = minHour; h <= maxHour; h++) {
                  xLabels.push(h);
                }

                // Y-axis labels
                const yLabels = [0, 0.25, 0.5, 0.75, 1].map(f => Math.round(maxVal * f));

                return (
                  <div className="relative" style={{ height: '300px' }}>
                    <svg
                      viewBox="0 0 1000 300"
                      className="w-full h-full"
                      preserveAspectRatio="none"
                      onMouseLeave={() => setHoveredPoint(null)}
                    >
                      {/* Grid lines */}
                      {[0, 1, 2, 3, 4].map(i => (
                        <line
                          key={i}
                          x1={offsetX}
                          y1={60 + i * 50}
                          x2={offsetX + chartWidth}
                          y2={60 + i * 50}
                          stroke="currentColor"
                          strokeOpacity="0.1"
                        />
                      ))}

                      {/* X-axis hour labels */}
                      {xLabels.map(h => {
                        const x = offsetX + ((h - minHour) / timeRange) * chartWidth;
                        return (
                          <text
                            key={h}
                            x={x}
                            y="290"
                            textAnchor="middle"
                            className={`text-xs fill-current ${t.textSecondary}`}
                            style={{ fontSize: '12px' }}
                          >
                            {h.toString().padStart(2, '0')}:00
                          </text>
                        );
                      })}

                      {/* Y-axis labels */}
                      {yLabels.map((val, i) => (
                        <text
                          key={i}
                          x="45"
                          y={265 - i * 50}
                          textAnchor="end"
                          className={`text-xs fill-current ${t.textSecondary}`}
                          style={{ fontSize: '11px' }}
                        >
                          {val}
                        </text>
                      ))}

                      {/* Area fills */}
                      {ticketArea && <path d={ticketArea} fill="rgba(233, 69, 96, 0.1)" />}
                      {visitorArea && <path d={visitorArea} fill="rgba(59, 130, 246, 0.1)" />}

                      {/* Lines */}
                      {ticketPath && <path d={ticketPath} fill="none" stroke="#e94560" strokeWidth="2" />}
                      {visitorPath && <path d={visitorPath} fill="none" stroke="#3b82f6" strokeWidth="2" />}

                      {/* Ticket scan points */}
                      {ticketPoints.map((p, i) => (
                        <circle
                          key={`t-${i}`}
                          cx={p.x}
                          cy={p.y}
                          r={hoveredPoint === `t-${i}` ? 6 : 3}
                          fill="#e94560"
                          stroke="white"
                          strokeWidth="1"
                          className="cursor-pointer"
                          onMouseEnter={() => setHoveredPoint(`t-${i}`)}
                        />
                      ))}

                      {/* Visitor points */}
                      {visitorPoints.map((p, i) => (
                        <circle
                          key={`v-${i}`}
                          cx={p.x}
                          cy={p.y}
                          r={hoveredPoint === `v-${i}` ? 6 : 3}
                          fill="#3b82f6"
                          stroke="white"
                          strokeWidth="1"
                          className="cursor-pointer"
                          onMouseEnter={() => setHoveredPoint(`v-${i}`)}
                        />
                      ))}
                    </svg>

                    {/* Tooltip for ticket points */}
                    {hoveredPoint !== null && hoveredPoint.startsWith('t-') && (() => {
                      const idx = parseInt(hoveredPoint.split('-')[1]);
                      const p = ticketPoints[idx];
                      if (!p) return null;
                      const xPercent = ((p.x - offsetX) / chartWidth) * 100;
                      return (
                        <div
                          className={`absolute ${t.card} border shadow-lg rounded-lg px-3 py-2 pointer-events-none z-10`}
                          style={{
                            left: `${Math.min(Math.max(xPercent, 5), 90)}%`,
                            top: '20px',
                            transform: 'translateX(-50%)'
                          }}
                        >
                          <p className={`text-sm font-semibold ${t.text}`}>
                            {p.hour.toString().padStart(2, '0')}:00
                          </p>
                          <p className="text-sm text-zf-accent font-bold">
                            {p.value.toLocaleString('nl-NL')} tickets gescand totaal
                          </p>
                        </div>
                      );
                    })()}

                    {/* Tooltip for visitor points */}
                    {hoveredPoint !== null && hoveredPoint.startsWith('v-') && (() => {
                      const idx = parseInt(hoveredPoint.split('-')[1]);
                      const p = visitorPoints[idx];
                      if (!p) return null;
                      const xPercent = ((p.x - offsetX) / chartWidth) * 100;
                      return (
                        <div
                          className={`absolute ${t.card} border shadow-lg rounded-lg px-3 py-2 pointer-events-none z-10`}
                          style={{
                            left: `${Math.min(Math.max(xPercent, 5), 90)}%`,
                            top: '20px',
                            transform: 'translateX(-50%)'
                          }}
                        >
                          <p className={`text-sm font-semibold ${t.text}`}>
                            {p.hour.toString().padStart(2, '0')}:00
                          </p>
                          <p className="text-sm text-blue-500 font-bold">
                            {p.value.toLocaleString('nl-NL')} bezoekers binnen totaal
                          </p>
                        </div>
                      );
                    })()}
                  </div>
                );
              })()}
            </div>
          ) : (
            <p className={`${t.textSecondary} text-center py-8`}>
              Geen data beschikbaar voor deze dag
            </p>
          )}
        </div>
      </div>

      {/* Hourly Summary Table */}
      <div>
        <h2 className={`text-xl font-semibold ${t.text} mb-4`}>Uuroverzicht Details</h2>
        <div className={`${t.card} rounded-xl shadow-lg p-6 border overflow-x-auto`}>
          {combinedHourly.length > 0 ? (
            <table className="w-full">
              <thead>
                <tr className={`border-b ${t.border}`}>
                  <th className={`text-left py-2 px-3 ${t.textSecondary} text-sm font-medium`}>Uur</th>
                  <th className={`text-right py-2 px-3 ${t.textSecondary} text-sm font-medium`}>Tickets Gescand</th>
                  <th className={`text-right py-2 px-3 ${t.textSecondary} text-sm font-medium`}>Bezoekers Binnen</th>
                </tr>
              </thead>
              <tbody>
                {combinedHourly.map((row, index) => (
                  <tr key={index} className={`border-b ${t.border} hover:${t.bgSecondary}`}>
                    <td className={`py-2 px-3 ${t.text}`}>{row.hour.toString().padStart(2, '0')}:00</td>
                    <td className="py-2 px-3 text-right font-medium text-zf-accent">
                      {row.ticketScans.toLocaleString('nl-NL')}
                    </td>
                    <td className="py-2 px-3 text-right font-medium text-blue-500">
                      {row.visitorIn.toLocaleString('nl-NL')}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className={`${t.bgSecondary}`}>
                  <td className={`py-2 px-3 font-bold ${t.text}`}>Totaal</td>
                  <td className="py-2 px-3 text-right font-bold text-zf-accent">
                    {combinedHourly.reduce((sum, h) => sum + h.ticketScans, 0).toLocaleString('nl-NL')}
                  </td>
                  <td className="py-2 px-3 text-right font-bold text-blue-500">
                    {combinedHourly.reduce((sum, h) => sum + h.visitorIn, 0).toLocaleString('nl-NL')}
                  </td>
                </tr>
              </tfoot>
            </table>
          ) : (
            <p className={`${t.textSecondary} text-center py-8`}>
              Geen data beschikbaar voor deze dag
            </p>
          )}
        </div>
      </div>

      {/* Kenteken overview card */}
      {kentekenStats && (
        <div>
          <h2 className={`text-xl font-semibold ${t.text} mb-4`}>Kentekens</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Totaal Kentekens"
              value={totals.totalKentekens}
              icon={<Car className="w-6 h-6" />}
              color="accent"
            />
            <StatCard
              title="Scans Vandaag"
              value={kentekenStats.scans_today || 0}
              icon={<BarChart3 className="w-6 h-6" />}
              color="success"
            />
            <StatCard
              title="Scans Deze Week"
              value={kentekenStats.scans_week || 0}
              icon={<BarChart3 className="w-6 h-6" />}
              color="accent2"
            />
            <StatCard
              title="Scans Deze Maand"
              value={kentekenStats.scans_month || 0}
              icon={<BarChart3 className="w-6 h-6" />}
              color="accent2"
            />
          </div>
        </div>
      )}

      {/* Info Panel */}
      <div className={`${t.card} rounded-xl shadow-lg p-4 border`}>
        <div className="flex items-center justify-between">
          <div>
            <p className={`${t.textSecondary} text-sm`}>Data bronnen</p>
            <p className={`${t.text} font-medium`}>MijnETickets, Personen Telling, Kenteken Scanner</p>
          </div>
          <div>
            <p className={`${t.textSecondary} text-sm text-right`}>Laatste update</p>
            <p className={`${t.text} font-medium text-right`}>
              {new Date().toLocaleString('nl-NL')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// StatCard component
const StatCard = ({ title, value, icon, color }) => {
  const { t } = useTheme();

  const colorClasses = {
    success: 'text-green-500',
    error: 'text-red-500',
    accent: 'text-zf-accent',
    accent2: 'text-blue-500',
  };

  return (
    <div className={`${t.card} rounded-xl shadow-lg p-6 border transition-all hover:shadow-xl`}>
      <div className="flex items-center justify-between">
        <div>
          <p className={`${t.textSecondary} text-sm mb-1`}>{title}</p>
          <p className={`${t.text} text-3xl font-bold`}>{value.toLocaleString('nl-NL')}</p>
        </div>
        <div className={colorClasses[color]}>
          {icon}
        </div>
      </div>
    </div>
  );
};

export default OverzichtDashboard;
